// RuminantWeightHistory — the weight history section shared by the cattle and
// sheep record pages (E3 residual: one component instead of two ~400-line
// copies in CowDetail and SheepDetail).
//
// The row math is the copies' math, unchanged: chronological rows, each
// compared with the previous weigh-in (days since, change, lb/day), shown
// newest first, plus a lifetime line. What changed is presentation only:
//
//   • a Table / Chart switch on the shared Segmented control;
//   • the table is a §3 card with Title Case column headers (Q35);
//   • the chart is the shared §4 trend line (WeightHistoryChart: brand green,
//     hollow past points, solid latest, white card on hover/focus/tap, D03/D04);
//   • ADG follows Table D (C3): only problems get colour and always with a
//     word — sheep warn "Low Gain" below 0.30 and danger "Losing Weight" below
//     0; cattle have no warn band until a target is set, so only "Losing
//     Weight" below 0. Good values stay black (D05);
//   • the cattle receiving weigh-in is a neutral "Receiving" badge.
//
// No data is read or written here: the page hands in its weigh-ins and its
// date formatter, exactly what the inline block used.

import React from 'react';
import Badge from '../shared/Badge.jsx';
import Segmented from '../shared/Segmented.js';
import WeightHistoryChart from '../shared/WeightHistoryChart.js';

type WeighInLike = {
  id?: string;
  entered_at?: string | null;
  weight?: unknown;
  session_id?: string | null;
  note?: string | null;
  [key: string]: unknown;
};

type Species = 'cattle' | 'sheep';

type AdgTone = {tone: 'danger' | 'warn'; word: string} | null;

/** Table D (C3) ADG thresholds. */
export function ruminantAdgTone(species: Species, value: number | null | undefined): AdgTone {
  if (value == null || !Number.isFinite(value)) return null;
  if (value < 0) return {tone: 'danger', word: 'Losing Weight'};
  if (species === 'sheep' && value < 0.3) return {tone: 'warn', word: 'Low Gain'};
  return null;
}

const TONE_COLOR = {danger: 'var(--danger)', warn: 'var(--warn-ink)'} as const;

type Row = {
  key: string;
  date: string;
  wt: number;
  dDays: number | null;
  dWt: number | null;
  lbDay: number | null;
  isReceiving: boolean;
};

const fmtSigned = (v: number | null | undefined, suffix?: string) =>
  v == null ? '—' : (v > 0 ? '+' : '') + v + (suffix || '');

function AdgValue({species, value, suffix}: {species: Species; value: number | null; suffix?: string}) {
  const tone = ruminantAdgTone(species, value);
  if (value == null) return <>—</>;
  return (
    <span style={{color: tone ? TONE_COLOR[tone.tone] : 'var(--ink)'}} data-adg-tone={tone ? tone.tone : 'none'}>
      {fmtSigned(value, suffix)}
      {tone && <span style={{display: 'block', fontSize: 11, fontWeight: 600}}>{tone.word}</span>}
    </span>
  );
}

const COLS = '120px 80px 80px 80px 100px';

export default function RuminantWeightHistory({
  weighIns,
  fmt,
  species,
}: {
  weighIns: WeighInLike[];
  fmt: (date: string) => React.ReactNode;
  species: Species;
}) {
  const [view, setView] = React.useState('table');
  if (!weighIns || weighIns.length === 0) return null;

  // Chronological (oldest→newest) so each weigh-in compares with the previous.
  const asc = [...weighIns].sort((a, b) => (a.entered_at || '').localeCompare(b.entered_at || ''));
  const rows: Row[] = asc.map((w, i) => {
    const d = (w.entered_at || '').slice(0, 10);
    const wt = parseFloat(String(w.weight)) || 0;
    const prev = i > 0 ? asc[i - 1] : null;
    const prevD = prev ? (prev.entered_at || '').slice(0, 10) : null;
    const prevW = prev ? parseFloat(String(prev.weight)) || 0 : 0;
    const dDays = prev
      ? Math.round((new Date(d + 'T12:00:00').getTime() - new Date(prevD + 'T12:00:00').getTime()) / 86400000)
      : null;
    const dWt = prev ? Math.round((wt - prevW) * 10) / 10 : null;
    const lbDay = prev && dDays != null && dWt != null && dDays > 0 ? Math.round((dWt / dDays) * 100) / 100 : null;
    // Cattle only: the first weigh-in of a receiving session is the arrival weight.
    const isReceiving =
      species === 'cattle' &&
      i === 0 &&
      ((w.session_id || '').startsWith('wsess-rcv-') || (w.note || '').toLowerCase().includes('receiving'));
    return {key: String(w.id ?? i), date: d, wt, dDays, dWt, lbDay, isReceiving};
  });
  const desc = [...rows].reverse();
  const first = rows[0];
  const last = rows[rows.length - 1];
  const lifetimeDays =
    first && last && first.date && last.date
      ? Math.round(
          (new Date(last.date + 'T12:00:00').getTime() - new Date(first.date + 'T12:00:00').getTime()) / 86400000,
        )
      : 0;
  const lifetimeWt = last && first ? Math.round((last.wt - first.wt) * 10) / 10 : 0;
  const lifetimeADG = lifetimeDays > 0 ? Math.round((lifetimeWt / lifetimeDays) * 100) / 100 : null;

  const lifetimeLine = rows.length > 1 && (
    <div
      data-weight-history-lifetime="1"
      style={{
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        alignItems: 'baseline',
        padding: '10px 14px',
        borderTop: '1px solid var(--border)',
        background: 'var(--surface-2)',
        fontSize: 12.5,
        color: 'var(--ink)',
      }}
    >
      <span style={{fontWeight: 700}}>Lifetime</span>
      <strong>
        {first.wt} → {last.wt} lb
      </strong>
      <span>{fmtSigned(lifetimeWt, ' lb')}</span>
      <span style={{color: 'var(--text-2)'}}>{'over ' + lifetimeDays.toLocaleString() + ' days'}</span>
      <strong>
        <AdgValue species={species} value={lifetimeADG} suffix=" lb/day" />
      </strong>
    </div>
  );

  return (
    <section style={{marginTop: 18}} data-weight-history={species}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 8}}>
        <h3 style={{margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--ink)'}}>Weight History ({rows.length})</h3>
        <Segmented
          options={[
            {value: 'table', label: 'Table'},
            {value: 'chart', label: 'Chart'},
          ]}
          value={view}
          onChange={setView}
          aria-label="Weight history view"
          data-weight-history-view={view}
        />
      </div>
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-card)',
          overflow: 'hidden',
          maxWidth: view === 'table' ? 660 : undefined,
        }}
      >
        {view === 'table' ? (
          <div style={{overflowX: 'auto'}}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: COLS,
                padding: '8px 14px',
                fontSize: 12.5,
                fontWeight: 700,
                color: 'var(--label)',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <div>Date</div>
              <div style={{textAlign: 'right'}}>Weight</div>
              <div style={{textAlign: 'right'}}>Days Since</div>
              <div style={{textAlign: 'right'}}>Change</div>
              <div style={{textAlign: 'right'}}>Lb / Day</div>
            </div>
            {desc.map((r, i) => (
              <div
                key={r.key}
                data-weight-history-row={r.key}
                style={{
                  display: 'grid',
                  gridTemplateColumns: COLS,
                  padding: '8px 14px',
                  fontSize: 13,
                  borderBottom: i < desc.length - 1 ? '1px solid var(--border)' : 'none',
                  fontVariantNumeric: 'tabular-nums',
                  alignItems: 'baseline',
                }}
              >
                <div style={{color: 'var(--ink)'}}>
                  {fmt(r.date)}
                  {r.isReceiving && (
                    <div style={{marginTop: 2}}>
                      <Badge variant="neutral">Receiving</Badge>
                    </div>
                  )}
                </div>
                <div style={{textAlign: 'right', fontWeight: 700, color: 'var(--ink)'}}>{r.wt.toLocaleString()} lb</div>
                <div style={{textAlign: 'right', color: 'var(--text-2)'}}>{r.dDays != null ? r.dDays : '—'}</div>
                <div style={{textAlign: 'right', color: 'var(--ink)'}}>{fmtSigned(r.dWt)}</div>
                <div style={{textAlign: 'right', fontWeight: 700}}>
                  <AdgValue species={species} value={r.lbDay} />
                </div>
              </div>
            ))}
            {lifetimeLine}
          </div>
        ) : (
          <>
            <div style={{padding: '12px 14px'}}>
              <WeightHistoryChart
                label="Weight history in pounds"
                fmtDate={fmt}
                points={rows.map((r) => ({
                  key: r.key,
                  date: r.date,
                  weight: r.wt,
                  detail:
                    r.dWt == null
                      ? r.isReceiving
                        ? 'Receiving weight'
                        : 'First recorded weight'
                      : `${fmtSigned(r.dWt, ' lb')} since previous weight` +
                        (r.lbDay != null ? ` · ${fmtSigned(r.lbDay, ' lb/day')}` : ''),
                }))}
              />
            </div>
            {lifetimeLine}
          </>
        )}
      </div>
    </section>
  );
}
